// ─────────────────────────────────────────────────────────────
//  Глобальный экземпляр Telegram WebApp SDK
//  Импортируйте `tg` или хелперы в любых модулях:
//    import tg, { haptic, getUser, openLink } from '../tg';
// ─────────────────────────────────────────────────────────────
import WebApp from '@twa-dev/sdk';

// Инициализация (вызывается один раз при загрузке main.jsx)
try {
  WebApp.ready();
  WebApp.expand();
  WebApp.setHeaderColor?.('#8C6FB0');
  WebApp.setBackgroundColor?.('#F6F0FA');
  // disableVerticalSwipes доступен с TG 7.7+, ловим ошибку
  WebApp.disableVerticalSwipes?.();
} catch {
  // вне TG-окружения SDK молчаливо отказывает — нам это OK
}

// Глобальный экземпляр
const tg = WebApp;
export default tg;

// ─────────── ХЕЛПЕРЫ ───────────

/** Тактильная отдача — light / medium / heavy / soft / rigid */
export const haptic = (style = 'light') => {
  try {
    tg.HapticFeedback?.impactOccurred?.(style);
  } catch {}
};

/** Селекшн-feedback (изменение состояния) */
export const hapticSelection = () => {
  try { tg.HapticFeedback?.selectionChanged?.(); } catch {}
};

/** Notification haptic — error / success / warning */
export const hapticNotification = (type = 'success') => {
  try { tg.HapticFeedback?.notificationOccurred?.(type); } catch {}
};

/** Юзер из initDataUnsafe (или null если открыто не из TG) */
export const getUser = () => tg.initDataUnsafe?.user || null;

/** Сырая initData для отправки на бэкенд (валидация подписи на сервере) */
export const getInitData = () => tg.initData || '';

/** Открыть внешнюю ссылку */
export const openLink = (url, opts = {}) => {
  try { tg.openLink?.(url, opts); }
  catch { window.open(url, '_blank'); }
};

/**
 * Открыть ссылку в системном браузере, а не во встроенном вебвью Telegram.
 *
 * Для платёжных страниц это не косметика: во встроенном браузере ломается всё, что
 * уводит пользователя из вкладки и возвращает обратно — 3-D Secure, редирект в
 * приложение банка, СБП. Наружу человек уходит, а вернуться ему некуда.
 *
 * `try_browser` понимают клиенты с Bot API 7.6+; на старых SDK молча его игнорирует
 * и открывает как умеет. На iOS Telegram и без него всегда уходит в системный
 * браузер, на Android — по умолчанию во встроенный, и подсказка нужна именно там.
 * `try_instant_view: false` — чтобы форму не попытались показать как статью.
 */
export const openExternalLink = (url) => {
  try {
    if (!tg.openLink) throw new Error('no openLink');
    tg.openLink(url, { try_instant_view: false, try_browser: 'chrome' });
  } catch {
    // Вне Telegram (браузер, локальная разработка) — обычная новая вкладка.
    window.open(url, '_blank', 'noopener,noreferrer');
  }
};

/** Открыть ссылку на TG (t.me/...) */
export const openTelegramLink = (url) => {
  try { tg.openTelegramLink?.(url); }
  catch { window.open(url, '_blank'); }
};

/** Показать всплывашку TG */
export const showAlert = (message) =>
  new Promise((resolve) => {
    try { tg.showAlert?.(message, resolve); }
    catch { alert(message); resolve(); }
  });

/** Подтверждение TG */
export const showConfirm = (message) =>
  new Promise((resolve) => {
    try { tg.showConfirm?.(message, (ok) => resolve(ok)); }
    catch { resolve(window.confirm(message)); }
  });

/** Управление MainButton TG */
export const mainButton = {
  show: (text, onClick) => {
    try {
      tg.MainButton?.setText(text);
      tg.MainButton?.onClick(onClick);
      tg.MainButton?.show();
    } catch {}
  },
  hide: () => { try { tg.MainButton?.hide(); } catch {} },
  setLoading: (on) => {
    try {
      if (on) tg.MainButton?.showProgress?.();
      else tg.MainButton?.hideProgress?.();
    } catch {}
  },
};

/** Управление BackButton TG */
export const backButton = {
  show: (onClick) => {
    try {
      tg.BackButton?.onClick(onClick);
      tg.BackButton?.show();
    } catch {}
  },
  hide: () => { try { tg.BackButton?.hide(); } catch {} },
};

/** Закрыть мини-апп */
export const closeApp = () => { try { tg.close(); } catch {} };

// Клиенты, на которых openTelegramLink подводит. Он не открывает ссылку сам, а шлёт
// клиенту событие `web_app_open_tg_link`, и что с ним делать, решает клиент: мобильные
// открывают канал в Telegram, десктопные — гасят мини-апп и никуда не переходят
// (проверено на Telegram для macOS). Список по значениям `tgWebAppPlatform`; 'unknown'
// тоже здесь — если платформу распознать не удалось, безопаснее не рисковать закрытием.
const NO_TG_LINK_PLATFORMS = new Set(['tdesktop', 'macos', 'weba', 'webk', 'web', 'unknown']);

/**
 * Увести пользователя в Telegram-канал.
 *
 * На мобильных — openTelegramLink: одно касание, канал открывается в самом Telegram.
 * На остальных — openLink: та же t.me-ссылка уходит в браузер, зато мини-апп остаётся
 * открытым. Это важнее, чем кажется: после закрытия приложения запасного хода у нас уже
 * нет, а так человек подписывается и возвращается на тот же экран, где под кнопкой ждёт
 * повторное «Забрать».
 */
export const openChannelLink = (url) => {
  if (!url) return;
  const platform = String(tg.platform || 'unknown').toLowerCase();
  if (!NO_TG_LINK_PLATFORMS.has(platform)) {
    try {
      if (tg.openTelegramLink) { tg.openTelegramLink(url); return; }
    } catch {}
  }
  try {
    if (tg.openLink) { tg.openLink(url); return; }
  } catch {}
  // Вне Telegram (локальная разработка) — обычная вкладка.
  window.open(url, '_blank', 'noopener,noreferrer');
};
